// ---------- le puzzle du jour ----------
// Un calendrier du mois, une feuille par jour, la même pour tout le monde. Le serveur affecte le
// puzzle d'un jour à la PREMIÈRE demande et ne le change plus (cf. backend/src/routes/daily.ts) ;
// cet écran ne fait donc que deux choses — montrer où on en est, et demander un jour.
//
// Ce qu'il ne fait PAS, et c'est un choix : aucune série, aucun compteur de jours d'affilée,
// aucune notification. `IDEES.md` §8 range « la récompense quotidienne culpabilisante » dans ce
// qu'il ne faut pas faire, avec les vies et les minuteries. Le calendrier montre ce qui a été
// peint ; il ne souligne pas les cases vides.

import { claimDaily, fetchDailyMonth, HttpError } from './api';
import type { DailyCell, DailyMonth } from './api';
import {
  dayLabel,
  dayTitle,
  monthTitle,
  t,
  weekdayInitials,
  WEEK_START,
  currentLang,
} from './i18n';
import { progressIndex } from './progress';
import { rebuildThumb } from './thumbs';
import { knownAlphabetIds } from '../shared/alphabets';

const $ = (id: string): HTMLElement => document.getElementById(id)!;

/** Ouvrir une feuille pour de bon — c'est `home.ts` qui sait le faire, et il passe sa fonction à
 *  `wireDaily()`. La dépendance pointe dans un seul sens (même forme que `setProgressSync` dans
 *  progress.ts) : ce module ne connaît ni l'état de jeu ni le catalogue. */
type Opener = (id: string, fx: number, fy: number) => Promise<void>;
let open: Opener | null = null;

/** Le mois affiché, en repère UTC — jamais un `Date` local : tout le mode vit en UTC (minuit UTC
 *  est le changement de jour), et mélanger les deux repères ferait décaler une case sur deux aux
 *  extrémités du mois. */
let view: { y: number; m: number } | null = null;
let data: DailyMonth | null = null;
/** La feuille du jour, telle que le serveur l'a affectée. Mémorisée À PART du mois affiché : la
 *  vedette parle d'AUJOURD'HUI, et reculer de trois mois pour regarder ce qu'on a peint ne doit
 *  pas la vider. Seul un mois qui contient le jour courant la remet à jour. */
let todayCell: DailyCell | null = null;
/** Le jour (repère UTC) où `data` a été chargé, selon l'APPAREIL. Une session qui traverse minuit
 *  — une nuit passée sur le catalogue suffit — laisse un cache dont le `today` est celui d'hier :
 *  ce champ est ce qui permet de s'en apercevoir sans redemander le mois à chaque tap d'onglet. */
let loadedOn: string | null = null;
/** Vrai depuis qu'une case a ouvert une feuille et jusqu'au retour au catalogue : c'est ce qui
 *  ramène sur CET onglet plutôt que sur une étagère (cf. `land()` dans home.ts). Une variable
 *  ici et non dans home.ts, parce que c'est ce module qui sait d'où le joueur est parti. */
let fromDaily = false;
/** Le jour de la case qui a ouvert la feuille. Le calendrier ouvre aussi les jours PASSÉS, et la
 *  carte de fin disait alors « le puzzle d'aujourd'hui » d'un puzzle du 3 septembre. */
let openedDay: string | null = null;

export function openedFromDaily(): boolean {
  return fromDaily;
}

/** La feuille ouverte est le puzzle du jour d'AUJOURD'HUI (jour UTC, celui du serveur), et non un
 *  jour rattrapé dans le calendrier. */
export function openedTodaysDaily(): boolean {
  return fromDaily && openedDay === deviceDay();
}

/** Appelé quand une ÉTAGÈRE ouvre une feuille : le retour ne doit alors plus revenir ici. */
export function forgetDailyOrigin(): void {
  fromDaily = false;
}

/**
 * Les 42 cases d'un mois : `null` pour le rembourrage, 'YYYY-MM-DD' pour un vrai jour.
 *
 * PURE et exportée pour être testée sans DOM ni réseau — c'est là que vivent les bugs de
 * calendrier (le mois qui commence un jour trop tôt, février d'une année bissextile, le
 * dimanche compté comme premier jour d'une semaine qui commence lundi).
 *
 * Toujours des semaines ENTIÈRES, et un nombre de lignes qui suit le mois (4, 5 ou 6) plutôt
 * qu'une grille figée à six : une ligne vide en bas déplacerait la barre d'onglets d'un mois à
 * l'autre.
 */
export function monthGrid(y: number, m: number, weekStart: 1 | 7): (string | null)[] {
  const first = new Date(Date.UTC(y, m, 1));
  // `getUTCDay()` rend 0 pour dimanche ; la numérotation ISO (1 = lundi … 7 = dimanche) est
  // celle de WEEK_START, d'où la conversion avant tout calcul de décalage.
  const iso = first.getUTCDay() === 0 ? 7 : first.getUTCDay();
  const lead = (iso - weekStart + 7) % 7;
  // Le 0 du mois suivant EST le dernier jour de celui-ci : la seule façon de compter les jours
  // d'un mois sans table ni règle bissextile à écrire.
  const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const cells: (string | null)[] = new Array(lead).fill(null);
  for (let d = 1; d <= days; d++) {
    cells.push(`${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7) cells.push(null);
  return cells;
}

/**
 * La saison d'un mois (0 = janvier), qui donne au calendrier sa teinte de trimestre.
 *
 * PURE et exportée pour la même raison que `monthGrid` : c'est une bascule de trimestre, et le
 * seul endroit où elle peut se tromper est décembre, qui appartient à l'hiver de l'année
 * SUIVANTE et donc au même groupe que janvier.
 *
 * Hémisphère nord, faute de mieux : rien dans l'application ne dit où est le joueur. C'est sans
 * conséquence tant que la teinte n'est pas NOMMÉE à l'écran (cf. la note de style.css) — un
 * lavis ambre en septembre à São Paulo n'annonce rien de faux, là où le mot « automne »
 * mentirait.
 */
export function seasonOf(m: number): 'hiver' | 'printemps' | 'ete' | 'automne' {
  if (m === 11 || m <= 1) return 'hiver';
  if (m <= 4) return 'printemps';
  if (m <= 7) return 'ete';
  return 'automne';
}

/** Le mois d'un jour 'YYYY-MM-DD', en repère UTC. */
function monthOf(day: string): { y: number; m: number } {
  return { y: Number(day.slice(0, 4)), m: Number(day.slice(5, 7)) - 1 };
}

/** Le jour courant selon l'APPAREIL, en repère UTC. Ce n'est JAMAIS une vérité — c'est le serveur
 *  qui dit quel jour on est (`DailyMonth.today`), et l'horloge d'un téléphone peut être réglée sur
 *  demain — seulement un SIGNAL : quand ce jour-ci a changé depuis le dernier chargement, le cache
 *  est périmé. Une horloge en avance ne coûte donc qu'une requête, dont la réponse recadre
 *  (cf. `load()`). */
function deviceDay(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Le mois à afficher quand le jour a tourné sous l'application.
 *
 * Suivre le nouveau jour SEULEMENT si l'écran montrait le mois courant — sinon minuit ramènerait
 * à aujourd'hui quelqu'un qui a reculé pour regarder ce qu'il a peint en septembre, et qui n'a
 * rien demandé de tel. Reculer s'arrête au mois de `epoch` (le 1er septembre 2026, la mise en
 * service du mode) : c'est la flèche « précédent » qui s'éteint là, et le serveur qui refuse.
 *
 * PURE et exportée pour la même raison que `monthGrid` : c'est un calcul de calendrier (le 31 au
 * 1er, décembre à janvier) qu'on ne veut pas avoir à reproduire dans un navigateur pour le voir
 * juste.
 */
export function viewAfterDayChange(
  view: { y: number; m: number },
  wasToday: string,
  now: string,
): { y: number; m: number } {
  const cur = monthOf(wasToday);
  return view.y === cur.y && view.m === cur.m ? monthOf(now) : view;
}

/** L'onglet vient d'être ouvert : on repart du mois courant SELON LE SERVEUR — jamais selon
 *  l'horloge du téléphone, qui peut être réglée sur demain (cf. `DailyMonth.today`). Le premier
 *  affichage n'a pas encore de réponse : on part du mois local, et la réponse recadre. */
export async function openDaily(): Promise<void> {
  const now = deviceDay();
  if (!view) view = monthOf(now);
  // Le jour a tourné pendant que l'application restait ouverte. Le cache porte alors le `today`
  // d'hier, et `cellFor()` traite la date du jour comme un FUTUR : une case muette, sans bouton,
  // où le puzzle d'aujourd'hui a l'air de n'être jamais arrivé. On jette ce cache — et on suit le
  // mois si minuit a aussi tourné la page (le 31 au 1er).
  else if (data && loadedOn !== now) {
    view = viewAfterDayChange(view, data.today, now);
    data = null;
    // La feuille mémorisée est celle d'hier : la garder ferait une vedette qui montre le dessin
    // de la veille sous la date du jour.
    todayCell = null;
  }
  // Repeindre depuis le cache quand on revient sur l'onglet : le mois ne change pas en changeant
  // d'onglet, et une requête par tap ferait clignoter la grille pour rien. Ce qui a pu changer,
  // c'est la progression — et elle est LOCALE, donc `paint()` la relit sans réseau.
  if (data) paint();
  else await load();
}

/** Recharge le mois affiché et repeint — appelé au retour d'une feuille, où la vignette et le
 *  pourcentage viennent de changer. Silencieux si l'onglet n'a jamais été ouvert. */
export function refreshDaily(): void {
  if (view) void load();
}

async function load(): Promise<void> {
  const v = view!;
  const month = `${v.y}-${String(v.m + 1).padStart(2, '0')}`;
  $('dailyBody').replaceChildren(state('dots', t('home.loading')));
  try {
    const got = await fetchDailyMonth(month);
    loadedOn = deviceDay();
    // Le mois demandé peut venir de l'horloge de l'APPAREIL (premier affichage, ou minuit passé
    // sous l'application) et donc être en avance sur celle du serveur, qui seule fait foi. Un mois
    // postérieur au sien n'a que des cases de futur et une flèche « suivant » éteinte à tort : on
    // recadre et on redemande. Une seule fois — le mois du serveur n'est jamais en avance sur
    // lui-même, donc ce second appel ne peut pas repasser ici.
    const cur = monthOf(got.today);
    if (v.y * 12 + v.m > cur.y * 12 + cur.m) {
      view = cur;
      return load();
    }
    data = got;
    // `days` ne porte que les jours DÉJÀ affectés : le jour courant peut n'y être pas encore, et
    // la vedette sait le dire (« personne ne sait encore ce que c'est »). D'où le `??` : un mois
    // ancien ne doit pas effacer ce qu'on sait d'aujourd'hui.
    todayCell = got.days.find((c) => c.day === got.today) ?? todayCell;
    paint();
    // Les vignettes manquantes se reconstruisent APRÈS le premier rendu, et sans être
    // attendues : la page doit s'afficher tout de suite, et un jour peint sur un autre appareil
    // n'est pas une erreur en attendant son image.
    void rebuildThumbs();
  } catch {
    $('dailyBody').replaceChildren(
      state('warn', t('home.empty.serverError'), t('home.empty.retry')),
    );
  }
}

/** Un état pleine page — chargement, panne — dans la même coquille que les états du catalogue,
 *  pour que les deux écrans se ressemblent. */
function state(icon: 'dots' | 'warn', text: string, cta?: string): HTMLElement {
  const el = document.createElement('div');
  el.className = `home-empty${icon === 'warn' ? ' err' : ''}`;
  el.innerHTML =
    icon === 'dots'
      ? `<span class="home-dots" aria-hidden="true"><i></i><i></i><i></i></span><p>${text}</p>`
      : `<span class="home-q"><svg class="i" aria-hidden="true"><use href="#i-warn"></use></svg></span><p>${text}</p>`;
  if (cta) {
    const b = document.createElement('button');
    b.className = 'btn';
    b.textContent = cta;
    b.onclick = () => void load();
    el.appendChild(b);
  }
  return el;
}

/** Ce que le joueur a déjà peint, par identifiant de feuille — l'index LOCAL, aucun réseau. */
function progressOf(id: string): { pct: number; done: boolean; thumb?: string } | undefined {
  return progressIndex().get(id);
}

function paint(): void {
  const v = view!;
  const d = data!;
  // Le titre et les deux flèches. La flèche « suivant » s'éteint sur le mois courant, celle de
  // « précédent » sur le mois de `epoch` : ce sont les deux bornes que le serveur refuse, et
  // proposer un geste qui reviendrait avec une erreur serait un mensonge de l'interface.
  const cur = monthOf(d.today);
  const min = monthOf(d.epoch);
  // La teinte suit le mois AFFICHÉ et non le mois courant : reculer d'un trimestre change la
  // couleur de l'écran, et c'est précisément ce qui fait qu'on voit qu'on a reculé.
  $('dailyPage').dataset['saison'] = seasonOf(v.m);
  paintToday(d);
  $('dailyMonth').textContent = monthTitle(new Date(Date.UTC(v.y, v.m, 1)));
  const prev = $('dailyPrev') as HTMLButtonElement;
  const next = $('dailyNext') as HTMLButtonElement;
  prev.disabled = v.y === min.y && v.m === min.m;
  next.disabled = v.y === cur.y && v.m === cur.m;

  const dows = $('dailyDows');
  dows.replaceChildren(
    ...weekdayInitials().map((w) => {
      const s = document.createElement('span');
      s.textContent = w;
      return s;
    }),
  );

  const byDay = new Map(d.days.map((c) => [c.day, c]));
  const grid = document.createElement('div');
  grid.className = 'daily-grid';
  for (const day of monthGrid(v.y, v.m, WEEK_START[currentLang()])) {
    grid.appendChild(day ? cellFor(day, byDay.get(day), d.today) : blank());
  }
  const body = $('dailyBody');
  body.replaceChildren(grid);
  const note = document.createElement('p');
  note.className = 'daily-note';
  note.id = 'dailyNote';
  body.appendChild(note);
}

/**
 * Le point d'interrogation de la vedette, dessiné EN POINTS plutôt qu'écrit : c'est la seule
 * chose que ce jeu sache dessiner, et un « ? » de police posé sur une feuille crème ressemblait
 * à un texte oublié là. Douze lignes de douze signes — la forme d'une petite grille, comme
 * n'importe quelle feuille du catalogue.
 *
 * Construit UNE FOIS au chargement du module : la vedette se repeint à chaque retour sur
 * l'onglet, et rebâtir vingt-six cercles à chaque fois serait du travail pour rien.
 */
const MYSTERE = ((): string => {
  const map = [
    '............',
    '...#####....',
    '..##...##...',
    '..##...##...',
    '.......##...',
    '......##....',
    '.....##.....',
    '.....##.....',
    '............',
    '.....##.....',
    '.....##.....',
    '............',
  ];
  let dots = '';
  map.forEach((row, y) =>
    [...row].forEach((c, x) => {
      if (c === '#') dots += `<circle cx="${x + 0.5}" cy="${y + 0.5}" r="0.4"/>`;
    }),
  );
  return `<svg viewBox="0 0 12 12" aria-hidden="true" fill="currentColor" opacity="0.45">${dots}</svg>`;
})();

/**
 * La vedette : la feuille du jour, au-dessus du calendrier.
 *
 * Elle ne DOUBLE pas la case d'aujourd'hui, elle la remplace comme porte d'entrée : les deux
 * mènent au même `tap()`, et c'est voulu — ce qui compte est qu'un écran de début de mois dise
 * enfin où taper, ce qu'une case parmi trente ne faisait pas.
 *
 * Elle parle d'aujourd'hui même quand on regarde octobre : c'est `todayCell` qu'elle lit, jamais
 * le mois affiché.
 */
function paintToday(d: DailyMonth): void {
  const host = $('dailyToday') as HTMLButtonElement;
  const p = todayCell ? progressOf(todayCell.id) : undefined;
  const encours = !!p && !p.done && p.pct > 0;
  // Deux lignes pour deux publics : `status` est ce que le lecteur d'écran annonce (le même
  // vocabulaire que les cases), `sub` ce qui s'écrit — et tant que rien n'est peint, écrire
  // « à peindre » sous un bouton qui dit « Peindre » serait le dire deux fois.
  const status = p?.done
    ? t('daily.state.done')
    : encours
      ? t('daily.state.doing', { pct: p!.pct })
      : t('daily.state.todo');
  const sub = p?.done || encours ? status : t('daily.today.unknown');
  const cta = p?.done
    ? t('daily.today.see')
    : encours
      ? t('daily.today.resume')
      : t('daily.today.paint');
  host.hidden = false;
  host.setAttribute('aria-label', t('daily.cell.aria', { date: dayLabel(d.today), state: status }));
  host.innerHTML = `
    <span class="dt-sheet">${p?.thumb ? `<img src="${p.thumb}" alt="">` : MYSTERE}</span>
    <span class="dt-txt">
      <span class="dt-k">${t('daily.today.kicker')}</span>
      <span class="dt-d">${dayTitle(d.today)}</span>
      <span class="dt-s">${sub}</span>
      <span class="dt-go">${cta}</span>
    </span>
    ${encours ? `<span class="dt-bar" aria-hidden="true"><i style="width:${p!.pct}%"></i></span>` : ''}
  `;
  host.onclick = (e) => void tap(d.today, todayCell ?? undefined, e.clientX, e.clientY);
}

/** Une case hors du mois : un trou dans la grille, et surtout pas un bouton. */
function blank(): HTMLElement {
  const el = document.createElement('span');
  el.className = 'daily-cell void';
  return el;
}

/**
 * Une case du mois. Quatre états, et un seul est cliquable deux fois de suite :
 *
 *  - FUTUR : une case, en lavis très pâle, et rien dedans que son numéro. Pas de bouton — le
 *    puzzle de demain n'existe pas encore, même pas côté serveur, et une case qu'on peut taper
 *    serait une promesse. Mais une CASE : vingt-cinq numéros nus posés sur le fond de l'écran,
 *    c'était tout le vide du début de mois.
 *  - PAS COMMENCÉ : un point d'encre au centre, qu'un puzzle soit déjà affecté à ce jour ou
 *    non. La case ne promet rien de plus que « il y a quelque chose à peindre ici », ce qui est
 *    vrai dans les deux cas — et l'affectation se fera au tap.
 *  - EN COURS / TERMINÉ : la feuille telle que le joueur l'a laissée. C'est la vignette locale,
 *    produite sur l'appareil (cf. renderThumb), jamais une image du serveur : elle ne révèle
 *    que ce qui a été peint.
 *
 * Le point suit donc l'IMAGE et non l'état : un jour joué sur un autre appareil a bien sa
 * progression mais pas encore sa vignette (elle est locale, cf. rebuildThumbs), et sans cette
 * règle sa case restait VIDE — ni image ni point — le temps de la reconstruire. Le sceau et la
 * jauge, eux, disent l'état dans les deux cas.
 */
function cellFor(day: string, cell: DailyCell | undefined, today: string): HTMLElement {
  if (day > today) {
    const el = document.createElement('span');
    el.className = 'daily-cell future';
    el.innerHTML = `<span class="dc-n">${Number(day.slice(8))}</span>`;
    return el;
  }
  const p = cell ? progressOf(cell.id) : undefined;
  const btn = document.createElement('button');
  btn.className = 'daily-cell';
  btn.dataset['day'] = day;
  btn.classList.toggle('today', day === today);
  const status = p?.done
    ? t('daily.state.done')
    : p && p.pct > 0
      ? t('daily.state.doing', { pct: p.pct })
      : t('daily.state.todo');
  btn.setAttribute('aria-label', t('daily.cell.aria', { date: dayLabel(day), state: status }));
  btn.title = btn.getAttribute('aria-label')!;
  if (p?.done) btn.classList.add('done');
  else if (p && p.pct > 0) btn.classList.add('doing');
  btn.innerHTML = `
    <span class="dc-n">${Number(day.slice(8))}</span>
    ${p?.thumb ? `<img class="dc-face" src="${p.thumb}" alt="">` : ''}
    ${p?.thumb ? '' : '<span class="dc-pip" aria-hidden="true"></span>'}
    ${p?.done ? '<span class="dc-seal" aria-hidden="true"><svg class="i"><use href="#i-seal"></use></svg></span>' : ''}
    ${p && !p.done && p.pct > 0 ? `<span class="dc-bar" aria-hidden="true"><i style="width:${p.pct}%"></i></span>` : ''}
  `;
  btn.onclick = (e) => void tap(day, cell, e.clientX, e.clientY);
  return btn;
}

/**
 * Le tap sur une case : obtenir la feuille de ce jour-là, puis l'ouvrir.
 *
 * Un seul chemin pour les trois états, et ce n'est pas une simplification : `openPuzzle()`
 * restaure déjà la progression enregistrée, donc une feuille en cours reprend où elle en était
 * et une feuille terminée s'ouvre sur sa page de fin — exactement comme depuis le catalogue.
 * Il n'y a rien à distinguer ici.
 */
async function tap(
  day: string,
  known: DailyCell | undefined,
  fx: number,
  fy: number,
): Promise<void> {
  if (!open) return;
  const note = document.getElementById('dailyNote');
  if (note) note.textContent = '';
  try {
    // Le POST n'a lieu que sur un jour encore libre : c'est lui qui GRAVE la feuille pour tout
    // le monde, et le refaire sur un jour connu serait une écriture pour rien.
    const cell = known ?? (await claimDaily(day));
    // L'alphabet du jour est le même pour la planète : cette version-ci ne sait peut-être pas le
    // dessiner. On le dit, plutôt que d'ouvrir une grille dont les cases n'auraient pas de
    // pastille (cf. la note de fetchPuzzles dans api.ts).
    if (!knownAlphabetIds().includes(cell.alphabetId)) {
      if (note) note.textContent = t('daily.empty.update');
      return;
    }
    fromDaily = true;
    openedDay = day;
    await open(cell.id, fx, fy);
  } catch (err) {
    // 409 = le stock de l'atelier est vide, ce qui n'est ni la faute du joueur ni celle de la
    // ligne ; tout le reste est un problème de réseau, et se dit avec le message qui existe déjà.
    const vide = err instanceof HttpError && err.status === 409;
    if (note) note.textContent = vide ? t('daily.empty.stock') : t('home.empty.serverError');
  }
}

/**
 * Les vignettes qui manquent : un jour peint sur un AUTRE appareil.
 *
 * La progression voyage (les taps sont synchronisés) mais la vignette est locale et ne voyage
 * pas — une réinstallation, ou le téléphone après la version web, affichait donc un « ? » sur un
 * jour pourtant fini. On la RECONSTRUIT : la grille du serveur, les taps qu'on a déjà, et le même
 * rasteriseur que la feuille en cours (renderThumbOf).
 *
 * EN SÉRIE et non en parallèle : c'est une requête et un canvas par jour, sur un téléphone, et
 * seule la première ouverture du mois les paie — la vignette reconstruite est écrite en local
 * (`setThumb`), donc le mois suivant s'affiche sans réseau.
 *
 * Un échec ne se voit pas : la case garde son « ? », qui est faux mais inoffensif, là où un
 * message d'erreur pour une image manquante serait du bruit.
 */
async function rebuildThumbs(): Promise<void> {
  const d = data;
  if (!d) return;
  for (const cell of d.days) {
    const p = progressOf(cell.id);
    if (!p || p.thumb || (!p.done && p.pct === 0)) continue;
    const url = await rebuildThumb(cell.id, p.done);
    if (!url) continue;
    // La case seule, et non la grille entière : repeindre effacerait le message d'erreur
    // éventuel et rejouerait l'apparition de toutes les autres.
    const btn = document.querySelector<HTMLElement>(`.daily-cell[data-day="${cell.day}"]`);
    const pip = btn?.querySelector('.dc-pip');
    if (btn && !btn.querySelector('.dc-face')) {
      const img = document.createElement('img');
      img.className = 'dc-face';
      img.alt = '';
      img.src = url;
      btn.appendChild(img);
      pip?.remove();
    }
  }
}

export function wireDaily(opener: Opener): void {
  open = opener;
  // Le téléphone qu'on rallume au matin sur le calendrier : même cache périmé que dans
  // `openDaily()`, mais sans le tap d'onglet qui le rafraîchirait. On ne redemande QUE si la page
  // est à l'écran et que le jour a bougé — recharger un mois que personne ne regarde serait une
  // requête pour rien, et c'est déjà ce que fait le tap d'onglet.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden || $('dailyPage').hidden) return;
    if (data && loadedOn !== deviceDay()) void openDaily();
  });
  // Les libellés des deux flèches sont posés par applyStaticI18n() depuis index.html
  // (`data-i18n-label`) : ici on ne câble que le geste.
  $('dailyPrev').onclick = () => step(-1);
  $('dailyNext').onclick = () => step(1);
}

/** Un mois en arrière ou en avant. Les bornes sont déjà tenues par l'état `disabled` des deux
 *  boutons ; ce calcul n'a donc qu'à traverser correctement janvier et décembre, ce que
 *  `Date.UTC` fait tout seul avec un mois hors bornes. */
function step(delta: number): void {
  if (!view) return;
  const d = new Date(Date.UTC(view.y, view.m + delta, 1));
  view = { y: d.getUTCFullYear(), m: d.getUTCMonth() };
  void load();
}
